import _ from 'lodash';

import {
  actionMeta as getActionMeta,
  actionName as getActionName,
} from 'app/service/ServiceUtils';
import { getNodeType } from 'app/beta-editor/utils/NodeUtils';
import getServiceAssociatedWithNode from 'app/beta-editor/utils/getServiceAssociatedWithNode';
import { PATH_DETAILS } from 'app/beta-editor/constants';
import { isPathNode } from 'app/beta-editor/utils/PathUtils';
import { prettyType } from 'app/common/CommonUtils';

import { FlowNode, FlowNodeType } from 'app/beta-editor/flowState';
import { Service, ServicesMap } from 'app/service/types';

const actionLabels = {
  default: {
    action: (node: FlowNode, service: Service) =>
      `${
        service.name ? `Select ${service.name} ${prettyType(node.type_of)}` : ''
      }`,
    app: 'Choose an App',
    fields: 'Options',
    type: 'Action',
  },
  filter: {
    action: 'Select Filter',
    fields: 'Filter Options',
    type: 'Filter',
  },
  paths_group: {
    action: '', // not used
    fields: '', // not used
    type: 'Paths',
  },
  read: {
    app: 'Choose a Trigger App',
    fields: 'Trigger Options',
    type: 'Trigger',
  },
  search: {
    app: 'Choose a Search App',
    fields: 'Search Options',
    type: 'Search',
  },
  search_or_write: {
    app: 'Choose a Search or Create App',
    fields: 'Search or Create Options',
    type: 'Search or Create',
  },
  write: {
    app: 'Choose an Action App',
    fields: (node, services) => {
      const service = getServiceAssociatedWithNode(node, services);
      const actionName = service
        ? getActionName(service, node.action, node.type_of, {
            hasNoVerb: false,
          })
        : '';
      return `${actionName} Template`;
    },
    type: 'Action',
  },
};

type Label =
  | 'Trigger'
  | 'Action'
  | 'Paths'
  | 'Search'
  | 'Filter'
  | 'Search or Create';

const getStepTypeLabel = (nodeType: FlowNodeType) =>
  _.get(actionLabels, [nodeType, 'type']);

const getLabel = (labelType, node, ...rest): Label => {
  const nodeType = getNodeType(node);
  const labels = actionLabels[nodeType] || {};
  const label = labels[labelType] || actionLabels.default[labelType];
  return typeof label === 'function' ? label(node, ...rest) : label;
};

const actionType = (node: FlowNode, ...rest: any[]) => {
  if (isPathNode(node)) {
    return PATH_DETAILS;
  }
  return getLabel('type', node, rest);
};

const pickAppLabel = _.partial(getLabel, 'app');

const pickActionLabel = _.partial(getLabel, 'action');

const fields = _.partial(getLabel, 'fields');

export const getActionLabelFromService = (
  node: FlowNode,
  service: Service | undefined
) => {
  const action = service
    ? getActionMeta(service, node.action, node.type_of)
    : '';
  return (action && action.label) || '';
};

const actionLabel = (node: FlowNode, services: ServicesMap) => {
  const service = getServiceAssociatedWithNode(node, services);
  return getActionLabelFromService(node, service);
};

const NodeLabels = {
  actionType,
  actionLabel,
  pickActionLabel,
  pickAppLabel,
  fields,
  getStepTypeLabel,
};

export default NodeLabels;
